import React from 'react'
import styles from './SortableHeader.module.scss'

export type SortDirection = 'asc' | 'desc'

interface SortableHeaderProps<T extends string> {
  /** The API's sort_by value for this column. */
  column: T
  /** Which column the list is currently ordered by. */
  activeColumn: T
  direction: SortDirection
  /**
   * Direction to apply when this column is picked up from cold. Dates and
   * states read newest-first; names read A-Z.
   */
  defaultDirection?: SortDirection
  onSort: (column: T, direction: SortDirection) => void
  children: React.ReactNode
}

/**
 * A clickable column header.
 *
 * Clicking the active column flips its direction; clicking any other adopts it
 * at its own default. The arrow shows only on the active column, but the space
 * for it is reserved on every header so the row does not shift when the sort
 * moves, and it fades in on hover to advertise that the header is clickable.
 */
export function SortableHeader<T extends string>({
  column,
  activeColumn,
  direction,
  defaultDirection = 'desc',
  onSort,
  children,
}: SortableHeaderProps<T>) {
  const isActive = column === activeColumn
  const nextDirection: SortDirection = isActive
    ? direction === 'asc'
      ? 'desc'
      : 'asc'
    : defaultDirection

  return (
    // aria-sort is only honoured on a columnheader; on the button itself it is
    // ignored, which left the sort state unannounced (the arrow is decorative).
    <div
      role="columnheader"
      aria-sort={
        isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <button
        type="button"
        className={styles.sortable}
        onClick={() => onSort(column, nextDirection)}
      >
        {children}
        <span
          className={`${styles.indicator} ${isActive ? '' : styles.inactive}`}
          aria-hidden="true"
        >
          {(isActive ? direction : defaultDirection) === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </div>
  )
}

export default SortableHeader
